import React, { useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { FilterColors } from "./CalendarFilterButton";
import { SessionModel } from "components/molecules/MonthCalendar";

export interface CourseScheduleApi {
  id: number;
  dateFrom: Date;
  dateTo: Date;
  session: SessionModel;
  placeType: number;
  placeName?: string;
  topicName?: string;
  topicGroupName?: string;
  examName?: string;
  trainersPrices?: {
    trainerId: number;
    trainerFirstName: string;
    trainerSurname: string;
  }[];
}
export interface CalendarEvent {
  key: string;
  eventName: string;
  eventColor: FilterColors;
  icon?: React.ReactNode;
  eventEnd?: Date;
  courseId: number;
  courseName: string;
  eventDescription?: string | React.ReactNode;
  eventStart: Date;
  rootObject?: CourseScheduleApi;
}
interface MonthCalendarProps {
  day: number;
  events: CalendarEvent[];
  onEventClick?: (event: CalendarEvent) => void;
  onMoreClick: (day: number, events: CalendarEvent[]) => void;
}
export const CalendarDayCard = ({ events, day, onMoreClick, onEventClick }: MonthCalendarProps) => {
  const today = new Date();
  const [month] = useState(today.getMonth());
  const [year] = useState(today.getFullYear());

  const isToday = (date: number): boolean => {
    const day = new Date(year, month, date);
    return today.toDateString() === day.toDateString() ? true : false;
  };

  return (
    <div className="h-30 relative overflow-hidden bg-white sm:h-28 lg:h-36 dark:bg-slate-800">
      <div className="flex h-full flex-col justify-between">
        <div className="relative flex grow flex-col overflow-hidden p-0.5 sm:p-1.5">
          {events.map((event) => {
            return (
              <button
                className="relative mb-1 w-full text-left"
                key={uuidv4()}
                onClick={() => {
                  if (onEventClick) onEventClick(event);
                }}
              >
                <EventBadge event={event} />
              </button>
            );
          })}
          <div
            className="pointer-events-none absolute bottom-0 left-0 right-0 h-4 bg-gradient-to-t from-white to-transparent dark:from-slate-800"
            aria-hidden="true"
          ></div>
        </div>
        <div className="flex items-center justify-between p-0.5 sm:p-1.5">
          {events.length > 2 && (
            <button
              onClick={() => onMoreClick(day, events)}
              className="whitespace-nowrap rounded border border-slate-200 px-0.5 text-center text-xs font-medium text-slate-500 sm:px-2 sm:py-0.5 dark:border-slate-700 dark:text-slate-300"
            >
              <span className="md:hidden">+</span>
              <span>{events.length - 2}</span> <span className="hidden md:inline">więcej</span>
            </button>
          )}
          <button
            className={`ml-auto inline-flex h-6 w-6 items-center justify-center rounded-full text-center text-xs font-medium hover:bg-indigo-100 sm:text-sm dark:text-slate-300 dark:hover:bg-slate-600 ${
              isToday(day) && "text-indigo-500"
            }`}
          >
            {day}
          </button>
        </div>
      </div>
    </div>
  );
};

export const EventBadge = ({ event }: { event: CalendarEvent }) => {
  const eventColor = (color: FilterColors) => {
    switch (color) {
      case "sky":
        return "text-white bg-sky-500";
      case "indigo":
        return "text-white bg-indigo-500";
      case "amber":
        return "text-white bg-amber-500";
      case "emerald":
        return "text-white bg-emerald-500";
      case "rose":
        return "text-white bg-rose-400";
      default:
        return "";
    }
  };
  return (
    <div className={`overflow-hidden rounded px-2 py-0.5 ${eventColor(event.eventColor)}`}>
      <div className="flex items-center truncate text-xs font-semibold">
        {event?.icon && event.icon}
        {event.eventName}
      </div>
      {event?.eventDescription && <div className="truncate text-xs">{event.eventDescription}</div>}
      <div className="hidden truncate text-xs uppercase sm:block">
        {event.eventStart && (
          <span>
            {event.eventStart.toLocaleTimeString("pl-PL", {
              hour: "2-digit",
              minute: "2-digit",
            })}
          </span>
        )}
        {event.eventEnd && (
          <span>
            -{" "}
            <span>
              {event.eventEnd.toLocaleTimeString("pl-PL", {
                hour: "2-digit",
                minute: "2-digit",
              })}
            </span>
          </span>
        )}
      </div>
    </div>
  );
};
